import { createResource, deleteResource, getResource, postgRESTApiUrl, readUrl } from "./api";
import { fetchWithTokenRefresh } from "./withTokenRefresh";

// Requests are created/updated via Rails API (/interact/api)
// Reads come from PostgREST API
// All functions throw on error and return unwrapped data
const create = async (attributes) => {
  const { post, ...rest } = attributes;
  const url = "/interact/api/requests";
  const data = { attributes: rest, ...(post !== undefined && { post }) };
  const result = await createResource(url, { data });

  if (result?.error) {
    throw new Error(`Failed to create request: ${result.error.status} ${result.error.statusText}`);
  }

  // Unwrap JSON:API format: result.data contains { data: { id, attributes } }
  if (result?.data?.data?.attributes) {
    const payload = result.data.data;
    return { id: payload.id, ...payload.attributes };
  }

  // If result.data is a plain object (not JSON:API wrapped), return it as-is
  if (result?.data) {
    return result.data;
  }

  throw new Error("Invalid response format from server");
};

const update = async (id, attributes, abortSignal = null) => {
  // 'post' belongs at data level; 'deduplication_key' is create-only
  // eslint-disable-next-line camelcase
  const { post, deduplication_key, ...cleanAttributes } = attributes;
  const data = { attributes: cleanAttributes, ...(post !== undefined && { post }) };

  const url = `/interact/api/requests/${id}`;
  const response = await fetchWithTokenRefresh(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
    ...(abortSignal && { signal: abortSignal }),
  });

  const body = await response.json();

  if (!response.ok) {
    throw new Error(`Failed to update request: ${response.status} ${response.statusText}`);
  }

  // Unwrap JSON:API format
  if (body?.data?.attributes) {
    const { data: responseData } = body;
    return { id: responseData.id, ...responseData.attributes };
  }

  // If body is a plain object (not JSON:API wrapped), return it as-is
  if (body && typeof body === "object") {
    return body;
  }

  throw new Error("Invalid response format from server");
};

const get = async (id) => {
  const url = readUrl("requests", { selector: `id=eq.${id}` });
  const result = await getResource(url);

  if (result.error) {
    throw new Error(`Failed to fetch request: ${result.error.status} ${result.error.statusText}`);
  }

  // PostgREST returns an array, extract first element
  const { data } = result;
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Request not found");
  }

  return data[0]; // Already unwrapped by PostgREST
};

// V1 request ids are V2 migrate_ids. media_profile_id is null unless the viewer may see the reporter.
const getByMigrateId = async (migrateId) => {
  const url = readUrl("requests", { selector: `select=id,media_profile_id&migrate_id=eq.${migrateId}&limit=1` });
  return getResource(url);
};

/**
 * Get the requests visible on a media profile via PostgREST RPC, one page at a time.
 *
 * The RPC is granted to authenticated users only; the server enforces entitlement
 * (admin, the profile's own user, or a paid-plan viewer) and returns [] otherwise.
 * Each row carries `total_count`, the number of requests matching the search.
 *
 * @param {number|string} mediaProfileId - The media profile ID to fetch requests for
 * @param {Object} [options]
 * @param {number} [options.limit] - Page size (the RPC defaults to 10, caps at 50)
 * @param {number} [options.offset] - Rows to skip
 * @param {"newest"|"oldest"} [options.sort] - Posting order (the RPC defaults to newest)
 * @param {string} [options.search] - Matches the title or details
 * @returns {Promise<{data: Array} | {error: Object}>}
 */
const getForMediaProfile = async (mediaProfileId, { limit, offset, sort, search } = {}) => {
  const url = `${postgRESTApiUrl()}/rest/v1/rpc/get_requests_for_media_profile`;
  const body = { p_media_profile_id: Number(mediaProfileId) };
  if (limit !== undefined) {
    body.p_limit = limit;
  }
  if (offset !== undefined) {
    body.p_offset = offset;
  }
  if (sort !== undefined) {
    body.p_sort = sort;
  }
  if (search) {
    body.p_search = search;
  }
  return createResource(url, body);
};

/**
 * Get the media outlets (with a request count each) for a media profile's rail card, via
 * PostgREST RPC.
 *
 * The RPC is callable by anyone (anon + authenticated); the server enforces the same
 * entitlement gate as getForMediaProfile (admin, the profile's own user, or a paid-plan
 * viewer) and returns [] otherwise.
 *
 * @param {number|string} mediaProfileId - The media profile ID to fetch outlet counts for
 * @returns {Promise<{data: Array} | {error: Object}>}
 */
const getMediaOutletsForMediaProfile = async (mediaProfileId) => {
  const url = `${postgRESTApiUrl()}/rest/v1/rpc/get_media_outlets_for_media_profile`;
  return createResource(url, { p_media_profile_id: Number(mediaProfileId) });
};

// Newest-posted first for the dropdown; pages arrive in id order (see listOwnedWithPitches).
const sortByPostedAtDesc = (rows) =>
  [...rows].sort((a, b) => String(b.posted_at || "").localeCompare(String(a.posted_at || "")));

// PostgREST caps every response at db-max-rows (1,000 in production), so a `limit` above it
// is silently clamped — the only way to see everything is to page. One row per REQUEST here
// (not per pitch), so the Posts filter's option list can never truncate in practice.
const PAGE = 1000;
// Keyset, NOT offset. PostgREST validates an offset range against an ESTIMATED row count and
// answers 416 when the offset passes it — and the estimate can be far below reality (measured
// locally: 3,501 real rows reported as 1,001, so paging died at offset 2000 and silently lost
// ~1,500 options). Keying off the last id seen has no such dependency. Same shape as
// pitches.listSentOnOwnRequests' cursor.
// The bound stands in for the short-page exit if the server ever ignores the cursor:
// 20 pages = 20,000 rows, far above any real profile.
const MAX_PAGES = 20;

const listOwnedWithPitches = async (mediaProfileId) => {
  const rows = [];
  let cursorId = null;
  for (let pageNo = 0; pageNo < MAX_PAGES; pageNo += 1) {
    const params = new URLSearchParams([
      ["own_request", "eq.true"], // eq, not is: only `= true` lets the planner use the index
      ["media_profile_id", `eq.${mediaProfileId}`],
      ["num_pitches", "gt.0"], // pitch_count is sent-only, so this mirrors "has pitches"
      ["select", "id,title,posted_at"],
      // Paging by id keeps the cursor total and NULL-free (posted_at is nullable and can tie);
      // display order is restored below, once every page is in.
      ["order", "id.desc"],
      ["limit", String(PAGE)],
      ...(cursorId ? [["id", `lt.${cursorId}`]] : []),
    ]);
    // eslint-disable-next-line no-await-in-loop -- keyset pages are sequential by nature
    const { data, error } = await getResource(readUrl("requests", { selector: params.toString() }));
    if (error) return { data: sortByPostedAtDesc(rows), error };
    // getResource maps a 416 (PostgREST count=estimated pagination discrepancy) to a
    // non-array `data` — spreading that would throw, so treat any non-array as the end.
    if (!Array.isArray(data)) return { data: sortByPostedAtDesc(rows) };
    rows.push(...data);
    if (data.length < PAGE) return { data: sortByPostedAtDesc(rows) };
    cursorId = data[data.length - 1].id;
  }
  return { data: sortByPostedAtDesc(rows) }; // bound hit
};

const destroy = async (id) => {
  const url = `/interact/api/requests/${id}`;
  const result = await deleteResource(url);

  if (result?.error) {
    throw new Error(`Failed to delete request: ${result.error.status} ${result.error.statusText}`);
  }

  return { success: true };
};

export default {
  create,
  update,
  get,
  getByMigrateId,
  getForMediaProfile,
  getMediaOutletsForMediaProfile,
  listOwnedWithPitches,
  destroy,
};
